import { Container } from 'unstated';
import * as XLSX from 'xlsx';
import { DentalCallType, DentalListRow, DentalPatientList, DentalImportSettings, DentalImportResult, DentalImportRequest, DentalProcedureCodeMatch } from '../../../../../shared/models/outbound/DentalPatientList';
import { PmsProcedureTypeModel } from '../../../../../shared/models/organizations/PmsLookupModels';
import * as outboundApi from '../../../../../shared/api/controllers/outboundCall';
import * as applicationsApi from '../../../../../shared/api/controllers/applications';
import ApplicationModel from '../../../../../shared/models/applications/api/ApplicationModel';
import * as medicalIntegrationApi from '../../../../../shared/api/controllers/medicalIntegrationConfiguration';
import { OutboundCallFeatureFlagId } from '../../../../../shared/constants/featureFlagIds';
import { applyProductionTypes, ColumnMapping, dentalListDefinitions, MAX_FILE_BYTES, MAX_LIST_ROWS, mergeProductionTypeMapping, parseProductionTypeMapping, procedureCodeMatches, readDentalSheet, savedProductionTypeIds, suggestMapping, validateDentalRows } from '../../../services/dentalPatientLists';
import { dentalRowDatesToUtc } from '../../../services/dentalImportDates';

const sameId = (first: string, second: string) => (first || '').toLowerCase() === (second || '').toLowerCase();

export const DentalImportStep = { Upload: 0, Columns: 1, ProcedureCodes: 2, Review: 3, Schedule: 4, Confirm: 5 };
const MedicalIntegrationConfigurationSettingId = 'a9c7e3f2-1b74-47cd-a830-942efab7c623';
const RecallProductionTypeMappingSettingId = 'a31c8f09-25da-4c4c-a230-70c216bd6452';
const RecallProductionTypeMappingSettingName = 'Recall Appointment ProductionType Mapping';

interface DentalPatientListState {
    step: number;
    type: DentalCallType;
    name: string;
    fileName: string;
    sheetName: string;
    sheetNames: string[];
    headers: string[];
    data: string[][];
    firstDataRow: number;
    mapping: ColumnMapping;
    rows: DentalListRow[];
    // Reactivation only: production types bookable online for the list's office, and procedure codes to match to them.
    productionTypes: PmsProcedureTypeModel[];
    productionTypeOfficeId: string;
    codeMatches: DentalProcedureCodeMatch[];
    // The match step only appears when a code still needs a production type chosen.
    codeStep: boolean;
    lists: DentalPatientList[];
    loading: boolean;
    error: string;
    excludeInvalid: boolean;
    issuesOnly: boolean;
    page: number;
    notice: string;
    settings?: DentalImportSettings;
    settingsLoading: boolean;
    settingsError: string;
    busy: boolean;
    startDate: string;
    earliestStartTime: string;
    latestEndTime: string;
    callsPerOfficePerHour: number;
    skipWeekends: boolean;
    assistantNumber: string;
    preview?: DentalImportResult;
    // The list just saved, shown in a confirmation dialog until dismissed.
    savedList?: DentalPatientList;
}

export default class DentalPatientListContainer extends Container<DentalPatientListState> {
    state: DentalPatientListState = {
        step: 0, type: 'outbound-reactivation', name: '', fileName: '', sheetName: '', sheetNames: [],
        headers: [], data: [], firstDataRow: 2, mapping: {}, rows: [], productionTypes: [], productionTypeOfficeId: '', codeMatches: [], codeStep: false, lists: [], loading: false, error: '',
        excludeInvalid: false, issuesOnly: false, page: 0, notice: '', settingsLoading: true, settingsError: '', busy: false,
        startDate: '', earliestStartTime: '09:00', latestEndTime: '17:00', callsPerOfficePerHour: 50, skipWeekends: false, assistantNumber: ''
    };
    private workbook: XLSX.WorkBook;
    private readVersion = 0;
    private nextListId = 0;
    private organizationId: string;
    private applicationId: string;
    private requestId: string;
    private previewRequest: DentalImportRequest;
    // Rows as validated from the sheet, before procedure codes are resolved, so the matches can be changed and re-applied.
    private sheetRows: DentalListRow[] = [];
    private savedCodeMatches = false;
    get availableDefinitions() { return dentalListDefinitions.filter(item => !(this.state.settings?.automatedCallTypes || []).includes(item.type)); }
    get definition() { return dentalListDefinitions.find(item => item.type === this.state.type)!; }
    update = (changes: Partial<DentalPatientListState>) => this.setState({ ...this.state, ...changes });

    get reviewBlocker(): string {
        if (!this.availableDefinitions.some(item => item.type === this.state.type)) return 'This call type is already scheduled automatically. Choose another call type.';
        if (!this.state.name.trim()) return 'Enter a list name to continue.';
        if (!this.state.rows.some(row => !row.errors.length && !row.duplicate)) return 'There are no valid new rows to schedule. Correct the spreadsheet or upload another list.';
        if (this.state.rows.some(row => row.errors.length) && !this.state.excludeInvalid) return 'Correct the rows with errors, or choose to exclude them before continuing.';
        if (this.state.settingsLoading) return 'Loading scheduling settings. You can continue once they have loaded.';
        if (!this.state.settings) return this.state.settingsError || 'Scheduling settings are unavailable. Retry loading them to continue.';
        return '';
    }

    initialize = async (organizationId: string, applicationId: string) => {
        this.organizationId = organizationId;
        this.applicationId = applicationId;
        await this.update({ settingsLoading: true, settingsError: '', settings: undefined });
        try {
            const result = await outboundApi.getDentalImportSettings(organizationId, applicationId);
            if (result.resultType !== 'Ok' || !result.data) {
                const fallback = result.httpStatus === 404 || result.resultType === 'NotFound'
                    ? 'Scheduling settings are unavailable. The connected API does not support spreadsheet imports yet. Run or deploy the updated Management API, then retry.'
                    : result.resultType === 'Unauthorized' ? 'Your session has expired. Sign in again to load scheduling settings.'
                    : result.resultType === 'PermissionDenied' ? 'You need permission to edit this organization before scheduling imports.'
                    : 'Unable to load scheduling settings. Retry, or contact your administrator if the problem continues.';
                throw new Error(result.resultType === 'Invalid' ? this.apiError(result, fallback) : fallback);
            }
            const available = dentalListDefinitions.filter(item => !(result.data.automatedCallTypes || []).includes(item.type));
            if (!available.some(item => item.type === this.state.type)) {
                this.requestId = undefined;
                this.previewRequest = undefined;
                const definition = available[0];
                await this.update({ type: definition?.type || this.state.type, step: DentalImportStep.Upload, preview: undefined, rows: [], codeMatches: [], codeStep: false,
                    mapping: definition ? suggestMapping(this.state.headers, definition) : {},
                    notice: this.state.fileName ? 'The selected call type is already automated. Choose an available type and review the columns again.' : this.state.notice });
            }
            await this.update({ settings: result.data, startDate: result.data.currentLocalDate,
                callsPerOfficePerHour: result.data.maxCallsPerOfficePerHour, assistantNumber: result.data.assistantNumbers[0] || '' });
        } catch (error) { await this.update({ settingsError: error.message || 'Unable to load scheduling settings. Retry to continue.' }); }
        finally { await this.update({ settingsLoading: false }); }
    };

    get scheduleValid(): boolean {
        const state = this.state;
        return !!state.settings && this.availableDefinitions.some(item => item.type === state.type) && !!state.startDate && state.startDate >= state.settings.currentLocalDate &&
            /^\d{2}:\d{2}$/.test(state.earliestStartTime) && /^\d{2}:\d{2}$/.test(state.latestEndTime) && state.earliestStartTime < state.latestEndTime &&
            Number.isInteger(state.callsPerOfficePerHour) && state.callsPerOfficePerHour >= 1 && state.callsPerOfficePerHour <= state.settings.maxCallsPerOfficePerHour &&
            state.settings.assistantNumbers.includes(state.assistantNumber);
    }

    changeSchedule = (changes: Partial<DentalPatientListState>) => {
        this.requestId = undefined;
        this.previewRequest = undefined;
        return this.update({ ...changes, preview: undefined, error: '' });
    };

    previewSchedule = async () => {
        if (!this.scheduleValid || this.state.busy) return;
        const accepted = this.state.rows.filter(row => !row.errors.length && !row.duplicate);
        if (!accepted.length || (!this.state.excludeInvalid && this.state.rows.some(row => row.errors.length))) return;
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);
        bytes[6] = (bytes[6] & 15) | 64;
        bytes[8] = (bytes[8] & 63) | 128;
        const hex = Array.from(bytes).map(value => value.toString(16).padStart(2, '0')).join('');
        this.requestId = this.requestId || `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
        await this.update({ busy: true, error: '' });
        try {
            const request: DentalImportRequest = {
                requestId: this.requestId, name: this.state.name.trim(), callType: this.state.type,
                startDate: this.state.startDate, earliestStartTime: this.state.earliestStartTime, latestEndTime: this.state.latestEndTime,
                callsPerOfficePerHour: this.state.callsPerOfficePerHour, skipWeekends: this.state.skipWeekends, assistantNumber: this.state.assistantNumber,
                rows: accepted.map(row => ({ ...dentalRowDatesToUtc(this.requestValues(row), this.state.settings.timeZone), rowNumber: row.rowNumber }))
            };
            const result = await outboundApi.previewDentalImport(this.organizationId, this.applicationId, request);
            if (result.resultType !== 'Ok' || !result.data) throw new Error(this.apiError(result, 'Unable to preview this schedule.'));
            this.previewRequest = request;
            await this.update({ preview: result.data, step: DentalImportStep.Confirm });
        } catch (error) { await this.update({ error: error.message }); }
        finally { await this.update({ busy: false }); }
    };

    // A reactivation procedure code only chooses the production type, so the API receives the resolved production type alone.
    private requestValues = (row: DentalListRow): Record<string, string> => {
        if (this.state.type !== 'outbound-reactivation') return row.values;
        const { procedureCode, ...values } = row.values;
        return values;
    };

    private apiError = (result: any, fallback: string): string =>
        (result.errors || []).map((error: any) => typeof error === 'string' ? error : error.message || error.description || '').filter(Boolean).join(' · ') || fallback;

    selectType = (type: DentalCallType) => {
        if (!this.availableDefinitions.some(item => item.type === type)) return;
        this.requestId = undefined;
        this.previewRequest = undefined;
        const definition = dentalListDefinitions.find(item => item.type === type)!;
        return this.update({ type, mapping: suggestMapping(this.state.headers, definition), rows: [], codeMatches: [], codeStep: false, preview: undefined, excludeInvalid: false, notice: '' });
    };

    reset = () => {
        this.readVersion++;
        this.workbook = undefined;
        this.requestId = undefined;
        this.previewRequest = undefined;
        this.sheetRows = [];
        this.savedCodeMatches = false;
        return this.update({ step: DentalImportStep.Upload, name: '', fileName: '', sheetName: '', sheetNames: [], headers: [], data: [], firstDataRow: 2, mapping: {}, rows: [], productionTypes: [], productionTypeOfficeId: '', codeMatches: [], codeStep: false, loading: false, error: '', excludeInvalid: false, issuesOnly: false, page: 0, preview: undefined });
    };

    loadFile = async (file: File) => {
        if (!file) return;
        const version = ++this.readVersion;
        this.workbook = undefined;
        await this.update({ loading: true, error: '', notice: '', fileName: '', sheetNames: [], headers: [], data: [], rows: [], mapping: {} });
        try {
            if (!/\.(xlsx|xls|csv)$/i.test(file.name)) throw new Error('Choose an Excel workbook (.xlsx or .xls) or a CSV file.');
            if (file.size > MAX_FILE_BYTES) throw new Error('This file is larger than 10 MB. Split it into smaller lists and try again.');
            const bytes = await new Promise<ArrayBuffer>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as ArrayBuffer);
                reader.onerror = () => reject(new Error('The file could not be read. Please try again.'));
                reader.readAsArrayBuffer(file);
            });
            if (version !== this.readVersion) return;
            const workbook = XLSX.read(bytes, { type: 'array', cellDates: true, dateNF: 'yyyy-mm-dd', sheetRows: MAX_LIST_ROWS + 3 });
            if (!workbook.SheetNames.length) throw new Error('This workbook has no worksheets.');
            this.workbook = workbook;
            await this.update({ fileName: file.name, sheetNames: workbook.SheetNames, name: file.name.replace(/\.[^.]+$/, '') });
            await this.selectSheet(workbook.SheetNames[0]);
        } catch (error) {
            if (version === this.readVersion) await this.update({ error: error.message || 'Unable to open this workbook. Save a new .xlsx copy and try again.' });
        } finally {
            if (version === this.readVersion) await this.update({ loading: false });
        }
    };

    selectSheet = async (sheetName: string) => {
        try {
            const { headers, data, firstDataRow } = readDentalSheet(this.workbook, sheetName);
            await this.update({ sheetName, headers, data, firstDataRow, mapping: suggestMapping(headers, this.definition), error: '', rows: [], page: 0 });
        } catch (error) {
            await this.update({ sheetName, error: error.message, headers: [], data: [], firstDataRow: 2, mapping: {}, rows: [] });
        }
    };

    get mappingComplete(): boolean {
        const { mapping } = this.state;
        const selected = Object.keys(mapping).map(key => mapping[key]).filter(value => value !== '');
        const mapped = (key: string) => mapping[key] !== '' && mapping[key] !== undefined;
        return this.definition.fields.every(field => !field.required || mapped(field.key))
            && (!this.definition.requireOneOf || this.definition.requireOneOf.some(mapped))
            && new Set(selected).size === selected.length;
    }

    get procedureCodesMatched(): boolean {
        return this.state.codeMatches.every(match => match.locked || match.productionTypeId);
    }

    get showsProcedureCodeStep(): boolean {
        return this.state.codeStep;
    }

    review = async () => {
        if (!this.mappingComplete || !this.availableDefinitions.some(item => item.type === this.state.type) || this.state.busy) return;
        this.requestId = undefined;
        this.previewRequest = undefined;
        this.savedCodeMatches = false;
        const reviewState = { excludeInvalid: false, issuesOnly: false, page: 0, error: '', preview: undefined as DentalImportResult };
        await this.update({ busy: true, error: '' });
        try {
            const usesAppOffice = this.definition.fields.some(field => field.defaultsFromApp && !this.state.mapping[field.key]);
            const booksProductionType = !!this.definition.booksProductionType;
            const application = usesAppOffice || booksProductionType ? await this.loadApplication() : undefined;
            const rows = validateDentalRows(this.state.data, this.state.mapping, this.definition, this.state.firstDataRow,
                usesAppOffice ? { officeId: this.appOfficeId(application) } : {});
            this.sheetRows = rows;
            if (!booksProductionType) return await this.update({ ...reviewState, rows, codeMatches: [], codeStep: false, step: DentalImportStep.Review });

            const offices = Array.from(new Set(rows.filter(row => !row.errors.length).map(row => row.values.officeId)));
            if (offices.length > 1) throw new Error(`This list has patients from ${offices.length} offices (${offices.join(', ')}). Upload one office per list.`);
            if (!offices.length) return await this.update({ ...reviewState, rows, codeMatches: [], codeStep: false, step: DentalImportStep.Review });

            const outboundFlag = this.outboundFlag(application);
            const configId = this.settingValue(outboundFlag, MedicalIntegrationConfigurationSettingId);
            if (!configId) throw new Error('Set the Medical Integration Configuration Id in this app’s outbound settings to check production types.');
            const savedMapping = parseProductionTypeMapping(this.settingValue(outboundFlag, RecallProductionTypeMappingSettingId));
            const result = await medicalIntegrationApi.getPmsProcedureTypes(configId, offices[0], true);
            if (result.resultType !== 'Ok' || !result.data) throw new Error(this.apiError(result, 'Unable to load production types from Denticon. Try again.'));
            if (!result.data.length) throw new Error(`No production types bookable online were found for office ${offices[0]}. Mark the production types to book as bookable online in Denticon, then try again.`);
            // Choices made before going back to the columns are kept while their production type is still bookable.
            const codeMatches = procedureCodeMatches(rows, savedMapping, result.data).map(match => {
                const previous = this.state.codeMatches.find(item => item.code === match.code && !item.locked);
                return !match.locked && previous && result.data.some(type => type.id === previous.productionTypeId) ? { ...match, productionTypeId: previous.productionTypeId } : match;
            });
            const codeStep = codeMatches.some(match => !match.locked);
            await this.update({ ...reviewState, productionTypes: result.data, productionTypeOfficeId: offices[0], codeMatches, codeStep,
                rows: codeStep ? rows : applyProductionTypes(rows, codeMatches, result.data, this.state.type),
                step: codeStep ? DentalImportStep.ProcedureCodes : DentalImportStep.Review });
        } catch (error) { await this.update({ error: error.message }); }
        finally { await this.update({ busy: false }); }
    };

    matchProcedureCode = (code: string, productionTypeId: string) => {
        if (!this.state.codeMatches.some(match => match.code === code && !match.locked)) return;
        this.requestId = undefined;
        this.previewRequest = undefined;
        return this.update({ codeMatches: this.state.codeMatches.map(match => match.code === code && !match.locked ? { ...match, productionTypeId } : match), preview: undefined });
    };

    applyProcedureCodes = () => {
        if (!this.procedureCodesMatched) return;
        return this.update({ rows: applyProductionTypes(this.sheetRows, this.state.codeMatches, this.state.productionTypes, this.state.type),
            step: DentalImportStep.Review, excludeInvalid: false, issuesOnly: false, page: 0, error: '' });
    };

    private loadApplication = async (): Promise<ApplicationModel> => {
        const result = await applicationsApi.findApplication(this.applicationId);
        if (result.resultType !== 'Ok' || !result.data) throw new Error(this.apiError(result, 'Unable to load this app’s outbound settings. Try again.'));
        return result.data;
    };

    private outboundFlag = (application: ApplicationModel): any => {
        const flag = (application.featureFlags || []).find((item: any) => sameId(item.id || item.featureFlagId, OutboundCallFeatureFlagId));
        if (!flag) throw new Error(`Outbound Calling is not enabled for this app. Enable it before importing ${this.definition.title.toLowerCase()} lists.`);
        return flag;
    };

    // The import API only accepts offices in the app's outbound office IDs, so a sheet without an office column needs exactly one.
    private appOfficeId = (application: ApplicationModel): string => {
        const offices = (application.outboundCallLocationId || '').split(/[,;\s]+/).filter(Boolean);
        if (!offices.length) throw new Error('Your spreadsheet has no Office ID column and this app has no outbound office ID configured. Add an Office ID column to the spreadsheet, or set the app’s outbound office ID, then try again.');
        if (offices.length > 1) throw new Error(`Your spreadsheet has no Office ID column and this app is set up for more than one office (${offices.join(', ')}). Add an Office ID column so each patient is matched to the right office.`);
        return offices[0];
    };

    // Application settings may carry the schema ID as featureFlagSettingId, and GUID casing can vary.
    private findSetting = (flag: any, settingId: string): any =>
        (flag.featureFlagSettings || []).find((setting: any) => sameId(setting.featureFlagSettingId || setting.id, settingId));

    private settingValue = (flag: any, settingId: string): string => (this.findSetting(flag, settingId)?.value || '').trim();

    // Re-reads the saved mapping right before writing so choices saved elsewhere are kept. A code saved under a different
    // production type since review stops the import, because the reviewed rows would book the other type.
    private saveProductionTypeMapping = async () => {
        const newMatches = this.state.codeMatches.filter(match => !match.locked);
        if (!newMatches.length) return;
        const flag = this.outboundFlag(await this.loadApplication());
        const setting = this.findSetting(flag, RecallProductionTypeMappingSettingId);
        const saved = parseProductionTypeMapping((setting?.value || '').trim());
        const conflict = newMatches.find(match => savedProductionTypeIds(saved, match.code).some(id => id !== match.productionTypeId));
        if (conflict) throw new Error(`Procedure code ${conflict.code} is now saved to production type ${savedProductionTypeIds(saved, conflict.code).join(', ')}, not ${conflict.productionTypeId}. Go back to Match columns and select Validate & review again to use the saved match. Nothing was scheduled.`);
        const merged = mergeProductionTypeMapping(saved, this.state.codeMatches);
        if (JSON.stringify(merged) !== JSON.stringify(saved)) {
            const result = await applicationsApi.updateApplicationFeatureFlagSettings(this.applicationId, {
                featureFlagId: flag.id || flag.featureFlagId || OutboundCallFeatureFlagId,
                featureFlagSettings: [{ id: setting?.featureFlagSettingId || setting?.id || RecallProductionTypeMappingSettingId, name: RecallProductionTypeMappingSettingName, value: JSON.stringify(merged) }]
            });
            if (result.resultType !== 'Ok') throw new Error(this.apiError(result, 'Unable to save your procedure code matches. Nothing was scheduled; try again.'));
        }
        // Saved matches can't be changed on a retry, so the rows and the mapping stay in agreement.
        this.savedCodeMatches = true;
        await this.update({ codeMatches: this.state.codeMatches.map(match => ({ ...match, locked: true })) });
    };

    closeSavedList = () => this.update({ savedList: undefined });

    prepare = async () => {
        if (!this.availableDefinitions.some(item => item.type === this.state.type) || this.state.busy || !this.previewRequest || !this.state.preview || this.state.step !== DentalImportStep.Confirm) return;
        const { rows, excludeInvalid, name } = this.state;
        if (!name.trim() || (!excludeInvalid && rows.some(row => row.errors.length))) return;
        const accepted = rows.filter(row => !row.errors.length && !row.duplicate);
        if (!accepted.length) return;
        await this.update({ busy: true, error: '' });
        try {
            await this.saveProductionTypeMapping();
            const result = await outboundApi.importDentalCalls(this.organizationId, this.applicationId, this.previewRequest);
            if (result.resultType !== 'Ok' || !result.data) throw new Error(this.apiError(result, 'Import failed. Your list is still here; you can retry safely.'));
            const savedRowNumbers = new Set(result.data.schedule.map(slot => slot.rowNumber));
            const list: DentalPatientList = {
                id: ++this.nextListId, name: name.trim(), type: this.state.type, fileName: this.state.fileName,
                sheetName: this.state.sheetName, preparedAt: new Date().toISOString(), rows: accepted.filter(row => savedRowNumbers.has(row.rowNumber)), schedule: result.data
            };
            await this.update({ lists: [list, ...this.state.lists], savedList: list, notice: `“${list.name}” saved: ${result.data.scheduledCount.toLocaleString()} calls scheduled, ${result.data.duplicatesExcluded.toLocaleString()} duplicates excluded.${this.savedCodeMatches ? ' Procedure code matches saved to this app’s outbound settings.' : ''} See Scheduled calls & history for saved calls.` });
            await this.reset();
        } catch (error) { await this.update({ error: error.message }); }
        finally { await this.update({ busy: false }); }
    };
}
